'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription,
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from './ui/dialog';
import { CheckCircle, AlertCircle } from 'lucide-react';

interface ApplyNowFormData {
  fullName: string;
  companyName: string;
  email: string;
  phoneNumber?: string;
  location: string;
  interest: string;
  message?: string;
}

interface ApplyNowFormProps {
  asModal?: boolean;
  triggerButton?: React.ReactNode;
  className?: string;
  defaultInterest?: string;
  defaultMessage?: string;
  requirePhoneNumber?: boolean;
}

export function ApplyNowForm({ 
  asModal = false, 
  triggerButton, 
  className = '',
  defaultInterest,
  defaultMessage,
  requirePhoneNumber = false
}: ApplyNowFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm<ApplyNowFormData>({
    defaultValues: {
      interest: defaultInterest || '',
      message: defaultMessage || ''
    }
  });

  const watchInterest = watch('interest');

  const onSubmit = async (data: ApplyNowFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://muslifin-website.oussama-ad8.workers.dev/api/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'apply',
          ...data,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const result = await response.json();
      
      if (result.success) {
        setSubmitStatus('success');
        reset();
        
        // Close modal after success if in modal mode
        if (asModal) {
          setTimeout(() => {
            setIsModalOpen(false);
            setSubmitStatus('idle');
          }, 2000);
        }
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const FormContent = () => (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl text-gray-900">Apply Now</h2>
        <p className="text-gray-600">
          Express your interest in working with MusliFin
        </p>
      </div>

      {/* Success Message */}
      {submitStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800">
            Thanks for reaching out! We'll be in touch soon.
          </p>
        </div>
      )}

      {/* Error Message */}
      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800">
            Something went wrong. Please try again.
          </p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-gray-700 whitespace-nowrap">
              Full Name *
            </Label>
            <Input
              id="fullName"
              {...register('fullName', { 
                required: 'Full name is required',
                minLength: {
                  value: 2,
                  message: 'Full name must be at least 2 characters'
                }
              })}
              className={`
                focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                ${errors.fullName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              `}
              placeholder="John Doe"
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm">{errors.fullName.message}</p>
            )}
          </div>

          {/* Company / Dealership Name */}
          <div className="space-y-2">
            <Label htmlFor="companyName" className="text-gray-700 whitespace-nowrap">
              Company Name *
            </Label>
            <Input
              id="companyName"
              {...register('companyName', { 
                required: 'Company name is required',
                minLength: {
                  value: 2,
                  message: 'Company name must be at least 2 characters'
                }
              })}
              className={`
                focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                ${errors.companyName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              `}
              placeholder="ABC Motors Inc."
            />
            {errors.companyName && (
              <p className="text-red-500 text-sm">{errors.companyName.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Email Address */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700 whitespace-nowrap">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              {...register('email', { 
                required: 'Email address is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address'
                }
              })}
              className={`
                focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                ${errors.email ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              `}
              placeholder="john@abcmotors.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email.message}</p>
            )}
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phoneNumber" className="text-gray-700 whitespace-nowrap">
              Phone Number{requirePhoneNumber ? ' *' : ''}
            </Label>
            <Input
              id="phoneNumber"
              type="tel"
              {...register('phoneNumber', {
                required: requirePhoneNumber ? 'Phone number is required' : false,
                pattern: {
                  value: /^[\+]?[1-9][\d]{0,15}$/,
                  message: 'Please enter a valid phone number'
                }
              })}
              className={`
                focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                ${errors.phoneNumber ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              `}
              placeholder="+1 (555) 123-4567"
            />
            {errors.phoneNumber && (
              <p className="text-red-500 text-sm">{errors.phoneNumber.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-gray-700 whitespace-nowrap">
              Location *
            </Label>
            <Input
              id="location"
              {...register('location', { 
                required: 'Location is required',
                minLength: {
                  value: 2,
                  message: 'Location must be at least 2 characters'
                }
              })}
              className={`
                focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                ${errors.location ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
              `}
              placeholder="Toronto, ON"
            />
            {errors.location && (
              <p className="text-red-500 text-sm">{errors.location.message}</p>
            )}
          </div>

          {/* I'm Interested In */}
          <div className="space-y-2">
            <Label htmlFor="interest" className="text-gray-700 whitespace-nowrap">
              I'm Interested In... *
            </Label>
            <Select 
              onValueChange={(value: string) => setValue('interest', value)}
              value={watchInterest || defaultInterest}
              defaultValue={defaultInterest}
            >
              <SelectTrigger 
                className={`
                  focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors
                  ${errors.interest ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}
                `}
              >
                <SelectValue placeholder="Select your interest" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Learning more about MusliFin as a Dealership">Learning more about MusliFin as a Dealership</SelectItem>
                <SelectItem value="Learning more about MusliFin as an Investor">Learning more about MusliFin as an Investor</SelectItem>
                <SelectItem value="Becoming a Partner">Becoming a Partner</SelectItem>
                <SelectItem value="Piloting Sayaara at my dealership">Piloting Sayaara at my dealership</SelectItem>
              </SelectContent>
            </Select>
            <input
              type="hidden"
              {...register('interest', { 
                required: 'Please select your interest',
                value: watchInterest || defaultInterest || ''
              })}
            />
            {errors.interest && (
              <p className="text-red-500 text-sm">{errors.interest.message}</p>
            )}
          </div>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <Label htmlFor="message" className="text-gray-700 whitespace-nowrap">
            Message
          </Label>
          <Textarea
            id="message"
            {...register('message')}
            className="focus:ring-2 focus:ring-[#004aad] focus:border-[#004aad] transition-colors min-h-[120px]"
            placeholder="Tell us more about your interest or any questions you have..."
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            type="submit"
            disabled={isSubmitting || submitStatus === 'success'}
            className="w-full bg-[#004aad] hover:bg-[#003a8a] text-white py-3 px-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Submitting...</span>
              </div>
            ) : (
              'Apply Now'
            )}
          </Button>
        </div>
      </form>
    </div>
  );

  if (asModal) {
    return (
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogTrigger asChild>
          {triggerButton || (
            <Button className="bg-[#004aad] hover:bg-[#003a8a] text-white">
              Apply Now
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-8">
          <DialogHeader className="sr-only">
            <DialogTitle>Apply Now Form</DialogTitle>
            <DialogDescription>
              Complete this form to express your interest in working with MusliFin
            </DialogDescription>
          </DialogHeader>
          <FormContent />
        </DialogContent>
      </Dialog>
    );
  }

  return <FormContent />;
}